import React, { useState, useEffect } from 'react';
import repairServicesData from '../assets/jewelry_repair_services.json';
import BookingModal from './BookingModal';

const RepairEstimator = () => {
  const [currentStep, setCurrentStep] = useState('category');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [answers, setAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [estimatedPrice, setEstimatedPrice] = useState(null);
  const [priceBreakdown, setPriceBreakdown] = useState([]);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  // Category icons mapping
  const categoryIcons = {
    'Ring Sizing': '💍',
    'Ring Repair': '🔧',
    'Earring / Pendant Repair': '💎',
    'Chain / Bracelet Repair': '🔗',
    'Watch Repair': '⌚',
    'Stone Setting / Replacement': '💠',
    'Cleaning & Polishing': '✨',
    'Pearl Services': '📿',
    'Soldering & Welding': '🔥',
    'Vintage & Antique Restoration': '🏺',
    'Custom Modifications': '⚒️',
    'Gemstone Services': '💎'
  };

  // Get available categories
  const categories = Object.keys(repairServicesData);

  // Get services for selected category
  const getServicesForCategory = () => {
    if (!selectedCategory) return [];
    return repairServicesData[selectedCategory];
  };

  // Get branches for selected service
  const getBranchesForService = () => {
    const services = getServicesForCategory();
    const service = services.find(s => s.service === selectedService);
    if (service) {
      // If service has branches, return them
      if (service.branches) {
        return service.branches;
      }
      // If no branches, treat the service itself as a branch
      return [{
        name: service.service,
        questions: service.questions,
        pricing_logic: service.pricing_logic,
        base_price: service.base_price,
        completion_time: service.completion_time,
        while_you_wait: service.while_you_wait,
        skip_to_booking: service.skip_to_booking,
        price_display: service.price_display,
        booking_message: service.booking_message
      }];
    }
    return [];
  };

  // Get current branch data
  const getCurrentBranch = () => {
    const branches = getBranchesForService();
    return branches.find(b => b.name === selectedBranch);
  };

  // Calculate dynamic price with ~ symbol
  const calculatePrice = () => {
    const result = calculatePriceWithAnswers(answers);
    return result.price;
  };

  // Get completion time and while-you-wait status
  const getServiceInfo = () => {
    const branch = getCurrentBranch();
    if (!branch) {
      // Try to get from service if branch not found
      const services = getServicesForCategory();
      const service = services.find(s => s.service === selectedService);
      if (service) {
        return {
          time: service.completion_time || '',
          whileYouWait: service.while_you_wait === true,
          complexityFactors: service.complexity_factors || []
        };
      }
      return { time: '', whileYouWait: false };
    }
    return {
      time: branch.completion_time || '',
      whileYouWait: branch.while_you_wait === true,
      complexityFactors: branch.complexity_factors || []
    };
  };

  // Handle question answers
  const handleAnswer = (questionKey, answer) => {
    setAnswers(prev => ({ ...prev, [questionKey]: answer }));
  };

  // Move to next question
  const nextQuestion = (latestAnswerKey, latestAnswer) => {
    const branch = getCurrentBranch();
    if (currentQuestionIndex < branch.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // All questions answered, calculate price with ~ symbol
      // Use the latest answer along with existing answers for calculation
      const allAnswers = { ...answers, [latestAnswerKey]: latestAnswer };
      const { price, breakdown } = calculatePriceWithAnswers(allAnswers);
      
      // Preserve scroll position when transitioning to estimate
      const currentScrollY = window.scrollY;
      
      setEstimatedPrice(price);
      setPriceBreakdown(breakdown);
      setCurrentStep('estimate');
      
      // Restore scroll position after state update
      setTimeout(() => {
        window.scrollTo(0, currentScrollY);
      }, 0);
    }
  };

  // Calculate price with specific answers
  const calculatePriceWithAnswers = (answersToUse) => {
    const branch = getCurrentBranch();
    if (!branch) return { price: '~$0', breakdown: [] };

    // Start with base price from JSON
    let totalPrice = branch.base_price;
    const pricingLogic = branch.pricing_logic;
    const breakdown = [{ label: 'Base Service', value: branch.base_price }];
    
    // Track size multiplier for Ring Sizing
    let sizeMultiplier = 1;
    let sizeAdjustmentLabel = '';
    
    // Apply pricing logic based on answers
    Object.keys(pricingLogic).forEach(key => {
      const userAnswer = answersToUse[key];
      
      if (pricingLogic[key] && userAnswer !== undefined) {
        // For Ring Sizing, capture size_adjustment as multiplier but don't add it
        if (key === 'size_adjustment' && selectedCategory === 'Ring Sizing') {
          sizeMultiplier = pricingLogic[key][userAnswer] || 1;
          sizeAdjustmentLabel = userAnswer;
          return; // Skip adding to total
        }
        // Handle nested conditionals (like band_width based on metal type)
        else if (typeof pricingLogic[key] === 'object' && !Array.isArray(pricingLogic[key])) {
          // Check if this is a nested structure with metal types as keys
          const metalType = answersToUse.metal_type || answersToUse.metal_modifier;
          if (metalType && pricingLogic[key][metalType] && typeof pricingLogic[key][metalType] === 'object') {
            // This is a nested conditional (like band_width based on metal type)
            if (pricingLogic[key][metalType][userAnswer] !== undefined) {
              const addValue = pricingLogic[key][metalType][userAnswer];
              if (addValue > 0) {
                breakdown.push({ 
                  label: `${key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}: ${userAnswer}`, 
                  value: addValue 
                });
              }
              totalPrice += addValue;
            }
          } else {
            // This is a regular object with direct pricing
            if (typeof pricingLogic[key][userAnswer] === 'number') {
              const addValue = pricingLogic[key][userAnswer];
              if (addValue > 0) {
                breakdown.push({ 
                  label: `${key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}: ${userAnswer}`, 
                  value: addValue 
                });
              }
              totalPrice += addValue;
            }
          }
        }
        // Handle multipliers (like metal_modifier)
        else if (key.includes('modifier') || key.includes('multiplier')) {
          const multiplier = pricingLogic[key][userAnswer];
          if (multiplier && multiplier !== 1) {
            breakdown.push({ 
              label: `${key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}: ${userAnswer}`, 
              value: `x${multiplier}` 
            });
            totalPrice *= multiplier;
          }
        }
        // Handle direct price additions (most common case)
        else if (typeof pricingLogic[key][userAnswer] === 'number') {
          const addValue = pricingLogic[key][userAnswer];
          if (addValue > 0) {
            breakdown.push({ 
              label: `${key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}: ${userAnswer}`, 
              value: addValue 
            });
          }
          totalPrice += addValue;
        }
      }
    });

    // Handle special fees that aren't question-based
    Object.keys(pricingLogic).forEach(key => {
      if (key.includes('fee') && typeof pricingLogic[key] === 'number') {
        totalPrice += pricingLogic[key];
      }
    });
    
    // Apply size multiplier for Ring Sizing services
    if (selectedCategory === 'Ring Sizing' && sizeMultiplier !== 1) {
      if (sizeAdjustmentLabel) {
        breakdown.push({ 
          label: `Size Adjustment: ${sizeAdjustmentLabel}`, 
          value: `x${sizeMultiplier}` 
        });
      }
      totalPrice = totalPrice * sizeMultiplier;
    }

    return { 
      price: `~$${Math.round(totalPrice)}`,
      breakdown: breakdown
    };
  };


  const resetEstimator = () => {
    setCurrentStep('category');
    setSelectedCategory('');
    setSelectedService('');
    setSelectedBranch('');
    setAnswers({});
    setCurrentQuestionIndex(0);
    setEstimatedPrice('');
    setPriceBreakdown([]);
    setIsBookingModalOpen(false);
  };

  // Simple positional mapping - question index matches pricing_logic key index
  const getQuestionKey = (question, questionIndex) => {
    const branch = getCurrentBranch();
    if (!branch || !branch.pricing_logic) return `question_${questionIndex}`;
    
    const pricingKeys = Object.keys(branch.pricing_logic);
    return pricingKeys[questionIndex] || `question_${questionIndex}`;
  };

  // Generic answer options - automatically uses pricing_logic keys
  const getAnswerOptions = (question) => {
    const questionKey = getQuestionKey(question, currentQuestionIndex);
    const branch = getCurrentBranch();
    
    if (!branch || !branch.pricing_logic || !branch.pricing_logic[questionKey]) {
      // Fallback for questions without pricing logic
      return ['Yes', 'No'];
    }
    
    const pricingLogic = branch.pricing_logic[questionKey];
    
    if (typeof pricingLogic === 'object' && !Array.isArray(pricingLogic)) {
      // Check if this is a nested conditional (like metal-dependent pricing)
      const firstKey = Object.keys(pricingLogic)[0];
      if (typeof pricingLogic[firstKey] === 'object' && !Array.isArray(pricingLogic[firstKey])) {
        // This is nested (e.g., band_width based on metal_type)
        const metalType = answers.metal_type || answers.metal_modifier;
        if (metalType && pricingLogic[metalType]) {
          return Object.keys(pricingLogic[metalType]);
        }
        // If no metal type selected yet, try to find a representative set of options
        const sampleOptions = Object.keys(pricingLogic[firstKey]);
        return sampleOptions.length > 0 ? sampleOptions : ['Yes', 'No'];
      }
      
      // Regular pricing object - return all the keys as options
      return Object.keys(pricingLogic);
    }
    
    // Fallback for unexpected structures
    return ['Yes', 'No'];
  };

  const isTextInput = (question) => {
    return question.toLowerCase().includes('current ring size') ||
           question.toLowerCase().includes('what size do you need') ||
           question.toLowerCase().includes('brand of the watch') ||
           question.toLowerCase().includes('what model') ||
           question.toLowerCase().includes('what is the brand');
  };

  return (
    <section id="repair-estimator" className="py-8 px-6 bg-gradient-to-b from-cream to-white">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <span className="font-montserrat text-xs tracking-super-wide text-gold-600 uppercase">Repair Services</span>
          <h2 className="font-playfair text-4xl md:text-5xl text-charcoal mt-4 mb-4">
            Get Your Repair Estimate
          </h2>
          
          <div className="w-24 h-px bg-gradient-gold mx-auto mb-6"></div>
          <p className="font-cormorant text-xl text-charcoal/70 max-w-2xl mx-auto mb-6">
            Professional jewelry and watch repairs completed on-site while you wait. Perfect for preparing engagement rings, anniversary jewelry, or holiday gifts. Get an instant estimate below.
          </p>
          
          {/* While You Wait Banner */}
          <div className="bg-gradient-to-r from-gold-500 to-gold-600 text-white px-6 py-3 rounded-full inline-block shadow-lg">
            <h3 className="font-montserrat text-sm font-bold tracking-wider">WHILE YOU WAIT SERVICE</h3>
            <p className="font-cormorant text-xs opacity-90 mt-1">Most repairs completed in 15-60 minutes</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-2xl overflow-hidden">
          {/* Progress Bar */}
          <div className="bg-charcoal px-8 py-4">
            <div className="flex items-center justify-between text-white text-sm">
              <span className={`${currentStep === 'category' ? 'text-gold-400' : 'text-white/50'}`}>1. Category</span>
              <span className={`${currentStep === 'service' ? 'text-gold-400' : 'text-white/50'}`}>2. Service</span>
              <span className={`${currentStep === 'branch' ? 'text-gold-400' : 'text-white/50'}`}>3. Details</span>
              <span className={`${currentStep === 'questions' ? 'text-gold-400' : 'text-white/50'}`}>4. Questions</span>
              <span className={`${currentStep === 'estimate' ? 'text-gold-400' : 'text-white/50'}`}>5. Estimate</span>
            </div>
          </div>

          <div className="p-8">
            {/* Step 1: Category Selection */}
            {currentStep === 'category' && (
              <div className="space-y-6">
                <div className="bg-gold-50 border border-gold-400/30 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-3 justify-center">
                    <span className="text-gold-600">⚡</span>
                    <p className="font-montserrat text-charcoal font-semibold">All repairs completed while you wait • Most services 15-60 minutes</p>
                    <span className="text-gold-600">⚡</span>
                  </div>
                </div>
                <h3 className="font-playfair text-2xl text-charcoal mb-6">What type of repair do you need?</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categories.map(category => (
                    <button
                      key={category}
                      onClick={() => {
                        setSelectedCategory(category);
                        const services = repairServicesData[category];
                        if (services.length === 1) {
                          // Skip service selection if only one service
                          setSelectedService(services[0].service);
                          const service = services[0];
                          
                          // Check if service should skip to booking
                          if (service.skip_to_booking) {
                            setSelectedBranch(service.service); // Set branch to service name for breadcrumb
                            setEstimatedPrice(service.price_display || `~$${service.base_price}`);
                            setPriceBreakdown([]);
                            setCurrentStep('estimate');
                          }
                          // Also check if we need to skip branch selection
                          else if (service.branches && service.branches.length === 1) {
                            setSelectedBranch(service.branches[0].name);
                            setCurrentStep('questions');
                          } else if (!service.branches) {
                            // Service has no branches, go straight to questions
                            setSelectedBranch(service.service);
                            setCurrentStep('questions');
                          } else {
                            setCurrentStep('branch');
                          }
                        } else {
                          setCurrentStep('service');
                        }
                      }}
                      className="p-6 border-2 border-gold-400/20 hover:border-gold-400 rounded-lg transition-all duration-300 text-left group hover:bg-gold-50"
                    >
                      <h4 className="font-playfair text-lg text-charcoal group-hover:text-gold-600 mb-2 text-center">
                        {category}
                      </h4>
                      <p className="font-montserrat text-charcoal/60 text-sm text-center">
                        {repairServicesData[category].length} service{repairServicesData[category].length > 1 ? 's' : ''} available
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Service Selection */}
            {currentStep === 'service' && (
              <div className="space-y-6">
                <div className="flex items-center mb-6">
                  <button onClick={() => setCurrentStep('category')} className="text-gold-600 hover:text-gold-700 mr-4">
                    ← Back
                  </button>
                  <h3 className="font-playfair text-2xl text-charcoal">Select Your {selectedCategory} Service</h3>
                </div>
                <div className="space-y-4">
                  {getServicesForCategory().map(service => (
                    <button
                      key={service.service}
                      onClick={() => {
                        setSelectedService(service.service);
                        // Check if service should skip to booking
                        if (service.skip_to_booking) {
                          setSelectedBranch(service.service); // Set branch to service name for breadcrumb
                          setEstimatedPrice(service.price_display || `~$${service.base_price}`);
                          setPriceBreakdown([]);
                          setCurrentStep('estimate');
                        } else {
                          setCurrentStep('branch');
                        }
                      }}
                      className="w-full p-6 border-2 border-gold-400/20 hover:border-gold-400 rounded-lg transition-all duration-300 text-left hover:bg-gold-50"
                    >
                      <h4 className="font-playfair text-xl text-charcoal mb-2">{service.service}</h4>
                      <p className="font-montserrat text-charcoal/60">{service.description}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Branch Selection */}
            {currentStep === 'branch' && (
              <div className="space-y-6">
                <div className="flex items-center mb-6">
                  <button onClick={() => {
                    // Skip back to category if only one service
                    const services = getServicesForCategory();
                    if (services.length === 1) {
                      setCurrentStep('category');
                    } else {
                      setCurrentStep('service');
                    }
                  }} className="text-gold-600 hover:text-gold-700 mr-4">
                    ← Back
                  </button>
                  <h3 className="font-playfair text-2xl text-charcoal">Choose Your Option</h3>
                </div>
                <div className="space-y-4">
                  {getBranchesForService().map(branch => (
                    <button
                      key={branch.name}
                      onClick={() => {
                        setSelectedBranch(branch.name);
                        // Check if branch should skip to booking
                        if (branch.skip_to_booking) {
                          setEstimatedPrice(branch.price_display || `~$${branch.base_price}`);
                          setPriceBreakdown([]);
                          setCurrentStep('estimate');
                        } else {
                          setCurrentStep('questions');
                        }
                      }}
                      className="w-full p-6 border-2 border-gold-400/20 hover:border-gold-400 rounded-lg transition-all duration-300 text-left hover:bg-gold-50"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="font-playfair text-xl text-charcoal">{branch.name}</h4>
                        <span className="font-montserrat text-gold-600 text-sm font-semibold">{branch.base_price_range}</span>
                      </div>
                      <div className="flex items-center gap-4 mb-2">
                        <div className="flex items-center gap-1">
                          <span className="text-gold-500">⏱️</span>
                          <span className="font-montserrat text-charcoal/70 text-sm">{branch.completion_time}</span>
                        </div>
                        <span className="bg-gold-500 text-white px-2 py-1 rounded-full text-xs font-montserrat font-semibold">
                          While You Wait!
                        </span>
                      </div>
                      {branch.brand_examples && (
                        <p className="font-montserrat text-charcoal/60 text-sm">
                          Examples: {branch.brand_examples.join(', ')}
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 4: Questions */}
            {currentStep === 'questions' && (
              <div className="space-y-6">
                <div className="flex items-center mb-6">
                  <button onClick={() => {
                    // Smart back navigation
                    const services = getServicesForCategory();
                    const branches = getBranchesForService();
                    
                    // If only one service and one/no branches, go to category
                    if (services.length === 1 && branches.length === 1) {
                      setCurrentStep('category');
                    } 
                    // If only one branch, check if we should skip to service or category
                    else if (branches.length === 1) {
                      if (services.length === 1) {
                        setCurrentStep('category');
                      } else {
                        setCurrentStep('service');
                      }
                    }
                    // Multiple branches, go back to branch selection
                    else {
                      setCurrentStep('branch');
                    }
                  }} className="text-gold-600 hover:text-gold-700 mr-4">
                    ← Back
                  </button>
                  <h3 className="font-playfair text-2xl text-charcoal">
                    Question {currentQuestionIndex + 1} of {getCurrentBranch()?.questions.length}
                  </h3>
                </div>
                
                {getCurrentBranch() && (
                  <div className="space-y-6">
                    <h4 className="font-cormorant text-xl text-charcoal">
                      {getCurrentBranch().questions[currentQuestionIndex]}
                    </h4>
                    
                    <div className="space-y-3">
                      {isTextInput(getCurrentBranch().questions[currentQuestionIndex]) ? (
                        <input
                          type="text"
                          placeholder="Enter your answer"
                          className="w-full p-4 border border-gold-400/30 focus:border-gold-400 rounded-lg"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && e.target.value.trim()) {
                              const questionKey = getQuestionKey(getCurrentBranch().questions[currentQuestionIndex], currentQuestionIndex);
                              handleAnswer(questionKey, e.target.value);
                              nextQuestion(questionKey, e.target.value);
                            }
                          }}
                        />
                      ) : (
                        getAnswerOptions(getCurrentBranch().questions[currentQuestionIndex]).map(option => (
                          <button
                            key={option}
                            onClick={() => {
                              const questionKey = getQuestionKey(getCurrentBranch().questions[currentQuestionIndex], currentQuestionIndex);
                              handleAnswer(questionKey, option);
                              nextQuestion(questionKey, option);
                            }}
                            className="w-full p-4 border border-gold-400/30 hover:border-gold-400 hover:bg-gold-50 rounded-lg transition-all duration-300 text-left"
                          >
                            {option}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 5: Estimate & Booking */}
            {currentStep === 'estimate' && (
              <div className="space-y-8">
                <div className="text-center">
                  <div className="text-6xl mb-4">{categoryIcons[selectedCategory]}</div>
                  <h3 className="font-playfair text-3xl text-charcoal mb-4">Your Repair Estimate</h3>
                  
                  <div className="bg-gradient-gold text-white px-8 py-4 rounded-lg inline-block mb-4">
                    <span className="text-3xl font-playfair">
                      {estimatedPrice}
                    </span>
                  </div>
                  
                  {/* Service Info Cards */}
                  <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                    {getServiceInfo().time && (
                      <div className="bg-cream px-4 py-3 rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className="text-gold-500">⏱️</span>
                          <span className="font-montserrat text-charcoal text-sm font-semibold">
                            {getServiceInfo().time}
                          </span>
                        </div>
                      </div>
                    )}
                    {getServiceInfo().whileYouWait && (
                      <div className="bg-gold-500 text-white px-4 py-3 rounded-lg">
                        <div className="flex items-center gap-2">
                          <span>🏆</span>
                          <span className="font-montserrat text-sm font-semibold">
                            While You Wait Service!
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="text-center mb-6">
                    <p className="font-montserrat text-sm text-charcoal/60">
                      {/* Show breadcrumb trail */}
                      {(() => {
                        // If category and service are the same (single service category)
                        if (selectedCategory === selectedService) {
                          return selectedCategory;
                        }
                        // If branch and service are the same (no branches)
                        if (selectedBranch === selectedService) {
                          return `${selectedCategory} → ${selectedService}`;
                        }
                        // Full path with branch
                        return `${selectedCategory} → ${selectedService} → ${selectedBranch}`;
                      })()}
                    </p>
                  </div>
                  
                  {/* Selection Summary - only show if there were questions */}
                  {getCurrentBranch() && getCurrentBranch().questions && getCurrentBranch().questions.length > 0 ? (
                    <div className="bg-white p-6 rounded-lg border border-gold-400/20 mb-6 max-w-2xl mx-auto">
                      <h4 className="font-playfair text-lg text-charcoal mb-4 text-center">Your Selections</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        {getCurrentBranch().questions.map((question, index) => {
                          const questionKey = getQuestionKey(question, index);
                          const answer = answers[questionKey];
                          if (answer) {
                            return (
                              <div key={questionKey} className="flex justify-between items-center py-2 border-b border-gold-100">
                                <span className="font-montserrat text-charcoal/70 text-left">{question.replace('?', '')}</span>
                                <span className="font-montserrat text-charcoal font-semibold text-right">{answer}</span>
                              </div>
                            );
                          }
                          return null;
                        })}
                      </div>
                    </div>
                  ) : (() => {
                    // Get the current service/branch data to check for booking_message
                    const services = getServicesForCategory();
                    const currentService = services.find(s => s.service === selectedService);
                    const currentBranch = getCurrentBranch();
                    
                    // Check branch first, then service
                    const bookingMessage = (currentBranch && currentBranch.booking_message) || 
                                         (currentService && currentService.booking_message);
                    
                    if (bookingMessage) {
                      return (
                        <div className="bg-gold-50 p-6 rounded-lg border border-gold-400/20 mb-6 max-w-2xl mx-auto">
                          <p className="font-montserrat text-charcoal text-center">
                            {bookingMessage}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  
                  <p className="font-cormorant text-lg text-charcoal/70 mb-8">
                    This estimate is based on your selections. Final pricing will be confirmed during inspection.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => setIsBookingModalOpen(true)}
                    className="px-8 py-4 bg-gradient-gold text-white font-montserrat text-sm tracking-wider hover:scale-105 transition-transform duration-300"
                  >
                    BOOK APPOINTMENT
                  </button>
                  <button
                    onClick={resetEstimator}
                    className="px-8 py-4 border border-gold-400 text-gold-600 font-montserrat text-sm tracking-wider hover:bg-gold-50 transition-colors duration-300"
                  >
                    NEW ESTIMATE
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <BookingModal
          isOpen={isBookingModalOpen}
          onClose={() => setIsBookingModalOpen(false)}
          isFromRepair={true}
          repairDetails={estimatedPrice ? {
            category: selectedCategory,
            service: selectedService,
            branch: selectedBranch,
            price: estimatedPrice,
            answers: answers
          } : null}
        />
      </div>
    </section>
  );
};

export default RepairEstimator;